import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listLookup } from '../../../api/masterData';
import { defaultNationalityId } from '../../../utils/nationality';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { finalizeStudent, getStudent, openStudentApplicationPdf } from '../../../api/students';
import { useStudentDraftAutosave } from './useStudentDraftAutosave';
import {
  buildStudentPayload,
  emptyWizardValues,
  isHealthDeclarationComplete,
  resultsToDrafts,
  studentToWizardValues,
  SubjectResultDraft,
  WizardValues,
} from './wizardTypes';
import { Step1ApplicationDetails } from './Step1ApplicationDetails';
import { Step2PersonalDetails } from './Step2PersonalDetails';
import { Step3AcademicDetails } from './Step3AcademicDetails';
import { Step4HigherEducation } from './Step4HigherEducation';
import { Step5PreferredStudy } from './Step5PreferredStudy';
import { Step6ParentsGuardian } from './Step6ParentsGuardian';
import { Step7VisaHistory } from './Step7VisaHistory';
import { Step8FinancialInformation } from './Step8FinancialInformation';
import { Step9Documents } from './Step9Documents';
import { Step10Declaration } from './Step10Declaration';
import { Step11Review } from './Step11Review';

const STEPS = [
  'Application Details',
  'Personal Details',
  'Academic Details',
  'Higher Education',
  'Preferred Study',
  'Parents / Guardian',
  'Visa History',
  'Financial Information',
  'Documents',
  'Declaration',
  'Review',
];
const DECLARATION_STEP = STEPS.indexOf('Declaration');

interface StudentApplicationWizardProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When set, reopens an existing Draft application where the counselor left off. */
  resumeStudentId?: number | null;
  /** Convert-to-Student workflow correction: when set, this application is
   * Lead-originated — the first autosave creates the draft Student with
   * this leadId (instead of a walk-in with none), and the last step saves
   * & closes ("Complete Application") rather than finalizing immediately.
   * The actual "Convert to Student" action happens separately, from the
   * Lead detail page's own confirmation dialog. */
  leadId?: number | null;
  /** Pre-fills a fresh (non-resume) application from data already captured
   * on the Lead/Personal Appointment, so the counselor isn't retyping what's
   * already on file. Ignored when resuming an existing draft. */
  leadPrefill?: Partial<WizardValues> | null;
}

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

export const StudentApplicationWizard = ({
  open,
  onClose,
  onSaved,
  resumeStudentId,
  leadId,
  leadPrefill,
}: StudentApplicationWizardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const autosave = useStudentDraftAutosave();

  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<WizardValues>(emptyWizardValues);
  // Sri Lanka as the starting nationality — only for a new record (a new
  // application or a draft still being completed) whose nationality is
  // empty, applied once per opening: a registered student's saved (or
  // empty) nationality is never changed, nor is a value the user cleared.
  const nationalityDefaultPending = useRef(false);
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
    enabled: open,
  });
  const [olResults, setOlResults] = useState<SubjectResultDraft[]>([]);
  const [alResults, setAlResults] = useState<SubjectResultDraft[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    if (resumeStudentId) {
      (async () => {
        const student = await getStudent(resumeStudentId);
        const resumed = studentToWizardValues(student);
        nationalityDefaultPending.current = student.isDraft && resumed.nationalityCountryId === '';
        setValues(resumed);
        setOlResults(resultsToDrafts(student.olResults));
        setAlResults(resultsToDrafts(student.alResults));
        autosave.hydrate(student);
        setActiveStep(Math.min(Math.max(student.lastCompletedStep, 0), STEPS.length - 1));
      })();
    } else {
      const fresh = { ...emptyWizardValues, ...(leadPrefill ?? {}) };
      nationalityDefaultPending.current = fresh.nationalityCountryId === '';
      setValues(fresh);
      setOlResults([]);
      setAlResults([]);
      autosave.reset();
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeStudentId]);

  useEffect(() => {
    if (!nationalityDefaultPending.current || !countriesQuery.data) return;
    nationalityDefaultPending.current = false;
    const id = defaultNationalityId(values.nationalityCountryId, countriesQuery.data);
    if (id != null) setValues((prev) => (prev.nationalityCountryId === '' ? { ...prev, nationalityCountryId: id } : prev));
  }, [countriesQuery.data, values]);

  const setField = <K extends keyof WizardValues>(field: K, value: WizardValues[K]) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const isStep2Valid = !!(values.firstName.trim() && values.lastName.trim() && values.phone.trim());
  const isDeclarationValid = isHealthDeclarationComplete(values);

  const handleClose = () => {
    // A draft may have been created/updated during this session even if the
    // counselor cancels out without finalizing — refresh the list so it shows up.
    if (autosave.studentId) onSaved();
    onClose();
  };

  const persistCurrentStep = async (lastCompletedStep: number) => {
    const payload = buildStudentPayload(values, olResults, alResults);
    return autosave.saveStep(payload, lastCompletedStep, leadId);
  };

  // Step 1 (Application Details) has no required fields of its own —
  // firstName/lastName/phone live on Step 2 — so there's nothing the
  // backend can persist yet (creation requires those). Its values still
  // get saved along with everything else the moment Step 2 completes.
  const canPersist = !!autosave.studentId || isStep2Valid;

  const handleSaveDraft = async () => {
    if (!canPersist) return;
    await persistCurrentStep(activeStep);
  };

  const handleNext = async () => {
    if (activeStep === 1 && !isStep2Valid) return;
    if (activeStep === DECLARATION_STEP && !isDeclarationValid) return;
    if (!canPersist) {
      setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
      return;
    }
    const student = await persistCurrentStep(activeStep + 1);
    if (!student) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const finalize = async (): Promise<number | null> => {
    if (!autosave.studentId) return null;
    setFinalizing(true);
    setLocalError(null);
    try {
      await persistCurrentStep(STEPS.length);
      await finalizeStudent(autosave.studentId);
      return autosave.studentId;
    } catch (err) {
      // Registration no longer waits for the Registration Fee (2026-11-06);
      // it is recorded later, on the student's Payments tab.
      setLocalError(extractErrorMessage(err));
      return null;
    } finally {
      setFinalizing(false);
    }
  };

  const handleRegisterStudent = async () => {
    const id = await finalize();
    if (id) {
      onSaved();
      onClose();
      navigate(`/students/${id}`);
    }
  };

  // Convert-to-Student workflow correction: a Lead-originated application
  // is deliberately never finalized from inside the wizard — completing it
  // only saves the last step and hands control back to the Lead detail
  // page, which is where the actual (separate, simple-confirmation)
  // Convert to Student action lives, once Registration Fee is also paid
  // and confirmed. This is the fix for the old flow, which finalized (and
  // so created a full Student, bypassing the Registration Fee gate)
  // the instant this dialog was submitted.
  const handleCompleteApplication = async () => {
    const student = await persistCurrentStep(STEPS.length);
    if (!student) return;
    onSaved();
    onClose();
  };

  const handlePrintPdf = async () => {
    if (autosave.studentId) await openStudentApplicationPdf(autosave.studentId);
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Student Application</DialogTitle>
      <Box sx={{ px: 3, pt: 1 }}>
        {isMobile ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {STEPS.length} · {STEPS[activeStep]}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((activeStep + 1) / STEPS.length) * 100}
              sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
            />
          </Box>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>

      <DialogContent dividers sx={{ minHeight: 460 }}>
        <Box sx={{ mb: 2 }}>
          {(autosave.error || localError) && <Alert severity="error">{autosave.error || localError}</Alert>}
          {autosave.saving && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
          {!autosave.saving && autosave.savedAt && (
            <Typography variant="caption" color="text.secondary">
              Draft saved {autosave.savedAt.toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        {activeStep === 0 && (
          <Step1ApplicationDetails
            values={values}
            setField={setField}
            studentId={autosave.studentId}
            applicationNumber={autosave.applicationNumber}
          />
        )}
        {activeStep === 1 && <Step2PersonalDetails values={values} setField={setField} />}
        {activeStep === 2 && (
          <Step3AcademicDetails
            values={values}
            setField={setField}
            olResults={olResults}
            setOlResults={setOlResults}
            alResults={alResults}
            setAlResults={setAlResults}
          />
        )}
        {activeStep === 3 && <Step4HigherEducation studentId={autosave.studentId} />}
        {activeStep === 4 && <Step5PreferredStudy values={values} setField={setField} />}
        {activeStep === 5 && <Step6ParentsGuardian values={values} setField={setField} />}
        {activeStep === 6 && <Step7VisaHistory values={values} setField={setField} />}
        {activeStep === 7 && <Step8FinancialInformation values={values} setField={setField} />}
        {activeStep === 8 && <Step9Documents studentId={autosave.studentId} />}
        {activeStep === 9 && <Step10Declaration values={values} setField={setField} studentId={autosave.studentId} />}
        {activeStep === 10 && <Step11Review studentId={autosave.studentId} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={handleSaveDraft} disabled={autosave.saving || !canPersist}>
          Save draft
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={autosave.saving || finalizing}>
            Previous
          </Button>
        )}
        {!isLastStep ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              autosave.saving ||
              (activeStep === 1 && !isStep2Valid) ||
              (activeStep === DECLARATION_STEP && !isDeclarationValid)
            }
          >
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" disabled={!autosave.studentId} onClick={handlePrintPdf}>
              Print / PDF
            </Button>
            {leadId ? (
              <Button variant="contained" disabled={autosave.saving || !autosave.studentId} onClick={handleCompleteApplication}>
                Complete Application
              </Button>
            ) : (
              <Button variant="contained" disabled={finalizing || !autosave.studentId} onClick={handleRegisterStudent}>
                {finalizing ? 'Saving…' : 'Register Student'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
